import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Download, Filter } from 'lucide-react';
import jsPDF from 'jspdf';
import api from '../api';

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

const toMinutes = (t) => {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

// Convert hex color (#rrggbb) → [r,g,b] for jsPDF
const hexToRgb = (hex) => {
  const h = (hex || '#d946ef').replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const groupKey = (s) => `${s.groupName}${s.level ? ' — ' + s.level : ''}`;

const SchedulePlanning = ({ type, seasonTitle }) => {
  const [slots, setSlots] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.get(`/schedule?type=${type}`).then((r) => setSlots(r.data || [])).catch(() => {});
  }, [type]);

  // Extract season string ("2026-2027") from registrationInfo.title (fallback: current season)
  const seasonLabel = useMemo(() => {
    if (seasonTitle) {
      const m = seasonTitle.match(/(\d{4}[-/]\d{4}|\d{4})/);
      if (m) return m[1].replace('/', '-');
    }
    const now = new Date();
    const y = now.getFullYear();
    const start = now.getMonth() >= 7 ? y : y - 1;
    return `${start}-${start + 1}`;
  }, [seasonTitle]);

  const heading = type === 'competition' ? 'Compétitions' : 'Loisirs';
  const title = `Planning ${heading} / saison ${seasonLabel}`;

  // Unique groups for the filter dropdown
  const groups = useMemo(() => {
    const map = new Map();
    slots.forEach((s) => { if (!map.has(groupKey(s))) map.set(groupKey(s), s.color); });
    return Array.from(map, ([label, color]) => ({ label, color }));
  }, [slots]);

  // Apply filter
  const visibleSlots = filter === 'all' ? slots : slots.filter((s) => groupKey(s) === filter);

  // Legend built from visible slots (kept for compétition)
  const legend = useMemo(() => {
    const seen = new Map();
    visibleSlots.forEach((s) => { if (!seen.has(s.groupName)) seen.set(s.groupName, s.color); });
    return Array.from(seen, ([name, color]) => ({ name, color }));
  }, [visibleSlots]);

  // Group slots per day / period
  const byDay = (day) => {
    const list = visibleSlots.filter((s) => s.day === day)
      .sort((a, b) => toMinutes(a.timeStart) - toMinutes(b.timeStart));
    const matin = list.filter((s) => toMinutes(s.timeStart) < 12 * 60);
    const aprem = list.filter((s) => toMinutes(s.timeStart) >= 12 * 60);
    return { matin, aprem };
  };

  const daysWithSlots = DAYS.filter((d) => {
    const { matin, aprem } = byDay(d);
    return matin.length + aprem.length > 0;
  });

  const exportPDF = () => {
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const marginX = 12;
    let y = 15;

    // Title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.setTextColor(30, 30, 30);
    pdf.text(title, pageW / 2, y, { align: 'center' });
    y += 6;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(120, 120, 120);
    pdf.text('GR La Saline — Gymnastique Rythmique', pageW / 2, y, { align: 'center' });
    y += 6;
    if (filter !== 'all') {
      pdf.setTextColor(160, 30, 130);
      pdf.text(`Groupe filtré : ${filter}`, pageW / 2, y, { align: 'center' });
      y += 6;
    }

    // Grid: N days columns
    const cols = daysWithSlots.length;
    if (cols === 0) {
      pdf.setTextColor(120);
      pdf.text('Aucun créneau pour cette sélection.', pageW / 2, y + 10, { align: 'center' });
      pdf.save(`planning-${type}-${seasonLabel}${filter !== 'all' ? '-' + filter.replace(/[^a-z0-9]+/gi, '-').toLowerCase() : ''}-gr-lasaline.pdf`);
      return;
    }

    const colW = (pageW - marginX * 2) / cols;
    const startY = y + 5;

    // Day headers
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(30, 30, 30);
    daysWithSlots.forEach((d, i) => {
      pdf.text(d.toUpperCase(), marginX + colW * i + colW / 2, startY, { align: 'center' });
    });

    const LINE_H = 3.4;
    const drawSection = (label, dayIdx, cursorY, list) => {
      if (list.length === 0) return cursorY;
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(8);
      pdf.setTextColor(140);
      pdf.text(label, marginX + colW * dayIdx + 2, cursorY);
      cursorY += 3;
      list.forEach((s) => {
        // Build wrapped lines: horaire (bold, 1 line), group name (bold, 1..N lines), level (normal, 1..N), lieu (normal, 1..N)
        const inner = colW - 8;
        const timeLines = [`${s.timeStart} – ${s.timeEnd}`];
        const groupLines = s.groupName ? pdf.splitTextToSize(s.groupName, inner) : [];
        const levelLines = s.level ? pdf.splitTextToSize(s.level, inner) : [];
        const locLines = s.location ? pdf.splitTextToSize('Lieu : ' + s.location, inner) : [];
        const nLines = timeLines.length + groupLines.length + levelLines.length + locLines.length;
        const cardH = Math.max(14, nLines * LINE_H + 3);

        // Page overflow guard: draw a "…" continuation and stop
        if (cursorY + cardH > pageH - 8) {
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(8);
          pdf.setTextColor(180);
          pdf.text('… (suite)', marginX + colW * dayIdx + 2, cursorY + 3);
          return cursorY + 5;
        }

        const [r, g, b] = hexToRgb(s.color);
        const bgR = Math.round(255 - (255 - r) * 0.18);
        const bgG = Math.round(255 - (255 - g) * 0.18);
        const bgB = Math.round(255 - (255 - b) * 0.18);
        pdf.setFillColor(bgR, bgG, bgB);
        pdf.rect(marginX + colW * dayIdx + 2, cursorY, colW - 4, cardH, 'F');
        pdf.setFillColor(r, g, b);
        pdf.rect(marginX + colW * dayIdx + 2, cursorY, 1.4, cardH, 'F');

        const textX = marginX + colW * dayIdx + 5;
        let ty = cursorY + 3;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(30, 30, 30);
        timeLines.forEach((l) => { pdf.text(l, textX, ty); ty += LINE_H; });

        pdf.setFontSize(9);
        groupLines.forEach((l) => { pdf.text(l, textX, ty); ty += LINE_H; });

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(90);
        levelLines.forEach((l) => { pdf.text(l, textX, ty); ty += LINE_H; });
        locLines.forEach((l) => { pdf.text(l, textX, ty); ty += LINE_H; });

        cursorY += cardH + 1.5;
      });
      cursorY += 2;
      return cursorY;
    };

    // For alignment, each day column has its own cursorY (independent stacking)
    const cursors = daysWithSlots.map(() => startY + 5);
    daysWithSlots.forEach((d, i) => {
      const { matin, aprem } = byDay(d);
      cursors[i] = drawSection('Matinée', i, cursors[i], matin);
      cursors[i] = drawSection('Après-midi', i, cursors[i], aprem);
    });

    // filename slug
    const groupSlug = filter !== 'all'
      ? '-' + filter.toLowerCase().replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')
      : '';
    pdf.save(`planning-${type}-${seasonLabel}${groupSlug}-gr-lasaline.pdf`);
  };

  if (slots.length === 0) return null;

  const SlotCard = ({ s }) => (
    <div
      className="rounded-lg p-2.5 flex flex-col gap-0.5 backdrop-blur-sm"
      style={{ backgroundColor: `${s.color}30`, borderLeft: `4px solid ${s.color}` }}
    >
      <div className="text-white/70 text-[11px] font-semibold">{s.timeStart} – {s.timeEnd}</div>
      <div className="text-white text-sm font-bold leading-tight break-words">{s.groupName}</div>
      {s.level && <div className="text-white/60 text-xs leading-tight break-words">{s.level}</div>}
      {s.location && <div className="text-white/50 text-xs leading-tight break-words">📍 {s.location}</div>}
    </div>
  );

  const DayHeader = ({ day }) => (
    <div className="text-center text-white font-bold uppercase tracking-wider text-sm pb-2 mb-3 border-b border-white/10">{day}</div>
  );

  const Band = ({ label, picker }) => {
    // picker(day) → list of slots for a given day within this band
    const hasAny = daysWithSlots.some((d) => picker(d).length > 0);
    if (!hasAny) return null;
    return (
      <div>
        <div className="mb-3 flex items-center gap-3">
          <span className="text-white/50 text-[10px] font-bold uppercase tracking-[0.25em]">{label}</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <div className="grid gap-3 md:gap-4" style={{ gridTemplateColumns: `repeat(${daysWithSlots.length}, minmax(0, 1fr))` }}>
          {daysWithSlots.map((day) => (
            <div key={day} className="min-w-0 space-y-2">
              {picker(day).map((s) => <SlotCard key={s.id} s={s} />)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="mb-16"
      data-testid={`planning-${type}`}
    >
      {/* Controls above the light card */}
      <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:justify-between mb-4">
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 max-w-full min-w-0">
          <Filter size={16} className="text-fuchsia-400 flex-shrink-0" />
          <label className="text-white/70 text-sm flex-shrink-0">Filtrer par groupe :</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid={`filter-${type}`}
            className="bg-transparent text-white text-sm border-0 focus:outline-none pr-6 min-w-0 max-w-[55%] sm:max-w-none truncate"
          >
            <option value="all" className="bg-neutral-900 text-white">Tous les groupes</option>
            {groups.map((g) => (
              <option key={g.label} value={g.label} className="bg-neutral-900 text-white">{g.label}</option>
            ))}
          </select>
        </div>
        <button
          onClick={exportPDF}
          data-testid={`export-planning-${type}`}
          className="flex items-center gap-2 px-4 py-2 bg-fuchsia-500/20 border border-fuchsia-500/40 rounded-lg text-fuchsia-300 text-sm hover:bg-fuchsia-500/30 transition-colors"
        >
          <Download size={16} /> Télécharger PDF
        </button>
      </div>

      <div
        className="relative rounded-3xl p-[1px]"
        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)' }}
      >
        <div
          className="rounded-3xl p-4 md:p-6 backdrop-blur-md"
          style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)' }}
        >
          <div className="mb-5 pb-3 border-b border-white/10">
            <h4 className="text-white text-xl md:text-2xl font-bold">{title}</h4>
            <p className="text-white/40 text-xs mt-1">GR La Saline — Gymnastique Rythmique</p>
            {filter !== 'all' && (
              <p className="text-fuchsia-300 text-xs mt-1">Filtre actif : <strong className="text-fuchsia-200">{filter}</strong></p>
            )}
          </div>

          {daysWithSlots.length === 0 ? (
            <p className="text-white/50 text-sm text-center py-8">Aucun créneau pour cette sélection.</p>
          ) : (
            <>
              {/* Desktop / tablet : two bands (Matinée + Après-midi) sharing the day columns */}
              <div className="hidden sm:block">
                {/* Row of day headers */}
                <div className="grid gap-3 md:gap-4 mb-4" style={{ gridTemplateColumns: `repeat(${daysWithSlots.length}, minmax(0, 1fr))` }}>
                  {daysWithSlots.map((day) => <DayHeader key={day} day={day} />)}
                </div>

                {/* Matinée band */}
                <Band label="Matinée" picker={(d) => byDay(d).matin} />

                {/* Big vertical space between the two bands */}
                <div className="h-12" aria-hidden />

                {/* Après-midi band */}
                <Band label="Après-midi" picker={(d) => byDay(d).aprem} />
              </div>

              {/* Mobile stacked (unchanged) */}
              <div className="sm:hidden space-y-5">
                {daysWithSlots.map((day) => {
                  const { matin, aprem } = byDay(day);
                  return (
                    <div key={day}>
                      <div className="text-white font-bold uppercase tracking-wider text-sm mb-2 border-b border-white/10 pb-1">{day}</div>
                      <div className="space-y-3">
                        {matin.length > 0 && (
                          <div>
                            <div className="text-white/40 text-[10px] font-bold uppercase tracking-[0.2em] mb-1.5">Matinée</div>
                            <div className="space-y-1.5">{matin.map((s) => <SlotCard key={s.id} s={s} />)}</div>
                          </div>
                        )}
                        {aprem.length > 0 && (
                          <div>
                            <div className="text-white/40 text-[10px] font-bold uppercase tracking-[0.2em] mb-1.5">Après-midi</div>
                            <div className="space-y-1.5">{aprem.map((s) => <SlotCard key={s.id} s={s} />)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default SchedulePlanning;
